// O CONTEXTO DA VISITA: de onde a pessoa veio, como chegou, e em que ela está.
//
// Até 02/10/2026 o SDK mandava três campos de contexto (`url`, `referrer`, `user_agent`) e a atribuição
// de marketing (fbclid/gclid/utm). Com isso dá para saber a campanha e nada sobre a visita: nem
// navegador, nem sistema, nem dispositivo, nem de que tipo de origem a pessoa veio, nem qual foi a
// página de entrada da sessão. Personalização que não sabe se é celular ou desktop não é
// personalização, é sorteio.
//
// ## O que entra aqui, e o que NÃO entra
//
// Entra o que o navegador EXPÕE por API padrão: `navigator.userAgentData` quando existe (a forma
// moderna, que não depende de adivinhar string), `navigator.userAgent` como reserva, idioma, fuso,
// tela, viewport, tipo de conexão, e o referrer já interpretado.
//
// NÃO entra nada que sirva para impressão digital: sem canvas, sem enumerar fonte, sem áudio, sem
// bateria, sem `plugins`. A diferença não é técnica, é de propósito — aquelas APIs existem para
// identificar a pessoa ATRAVÉS de sites, e esta não é a pergunta do produto. O que o produto precisa é
// saber em que contexto a visita aconteceu, e isso cabe no que o navegador conta de bom grado.
//
// ## AS CHAVES DO FIO SÃO EM INGLÊS, e a prosa daqui não
//
// O código desta casa é em português; o CONTRATO com o cliente não. Medido em 02/10/2026, nos eventos
// que já estão no lake:
//
//     codeandsoul   percent, label, tag, classes, href, seconds, section, viewport_width/height
//     agorafecha    device_type, page_path, page_name, viewport_width/height
//     o contrato    event_id, type, client_ts, user_id, anonymous_id, session_id, url, referrer
//
// Os dois produtores que existem já mandam inglês, e o schema publicado é inglês. Eu havia escrito
// `profundidade`, `texto`, `dispositivo` — um TERCEIRO dialeto para a mesma pergunta, que obrigaria
// todo consumidor (KPI, condição de regra, consulta) a ler dois nomes do mesmo campo. É a doença que
// este repositório persegue, e eu a introduzi.
//
// Onde já existe nome no lake, ele vence: `device_type` é do `agorafecha`, `percent` e `seconds` são do
// `codeandsoul`. Onde não existe, o inglês do contrato.
//
// ## Adaptadores injetáveis
//
// Nada aqui toca `window` direto: tudo vem por parâmetro, como `attribution.js` já faz. É o que torna
// o módulo testável sem navegador e é o idioma deste SDK.

/** Buscadores conhecidos, para classificar a origem. Lista curta e por HOST, nunca por parâmetro. */
const BUSCA = ["google.", "bing.", "duckduckgo.", "yahoo.", "ecosia.", "brave.", "yandex.", "baidu."];
const SOCIAL = ["facebook.", "instagram.", "l.instagram.", "twitter.", "t.co", "x.com", "linkedin.",
                "lnkd.in", "tiktok.", "pinterest.", "reddit.", "youtube.", "whatsapp.", "wa.me",
                "telegram.", "t.me", "threads."];
const EMAIL = ["mail.google.", "outlook.", "mail.yahoo.", "superhuman.", "hey.com"];

/** O tipo da origem, a partir do host do referrer e do host da própria página. */
export function tipoDaOrigem(referrer, hostDaPagina) {
  // OS VALORES SÃO OS DA INDÚSTRIA, não traduções minhas. `direct`, `organic`, `referral`, `social`,
  // `email` são os nomes que o GA, o Adobe e o Matomo usam, e eles viajam no fio sob `referrer_type`
  // para serem comparados numa condição de regra pelo analista do inquilino. Eu havia escrito
  // `direta`, `busca`, `referencia`, `interna`, `desconhecida` — o mesmo erro das chaves, uma camada
  // abaixo: a chave em inglês e o valor em português não resolvem nada, porque quem filtra escreve o
  // valor. `organic` em vez de `search` também é de propósito: `search` já é a chave da querystring
  // nos 802 `session_start` do tenant `synqi3`, e um nome só para duas perguntas é a doença seguinte.
  if (!referrer) return "direct";
  let host;
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return "unknown";                         // referrer ilegível: não é "direct", que afirmaria algo
  }
  if (hostDaPagina && host === String(hostDaPagina).toLowerCase()) return "internal";
  const casa = (lista) => lista.some((d) => host === d.replace(/\.$/, "") || host.includes(d));
  // A ORDEM IMPORTA, e isto foi medido: `mail.google.com` contém `google.`, então com BUSCA primeiro o
  // Gmail virava tráfego de busca. O mais ESPECÍFICO vem antes do mais genérico, sempre.
  if (casa(EMAIL)) return "email";
  if (casa(SOCIAL)) return "social";
  if (casa(BUSCA)) return "organic";
  return "referral";
}

/** O host do referrer, ou `undefined`. Nunca o caminho: caminho de outro site não é nosso assunto. */
export function hostDaOrigem(referrer) {
  if (!referrer) return undefined;
  try {
    return new URL(referrer).hostname.toLowerCase() || undefined;
  } catch {
    return undefined;
  }
}

//: Pares de reconhecimento do navegador, na ORDEM que importa: o primeiro que casa vence, e os
//: derivados vêm antes dos genéricos (Edge contém "Chrome", Chrome contém "Safari").
const NAVEGADORES = [
  [/\bEdg(?:e|A|iOS)?\/([\d.]+)/, "Edge"],
  [/\bOPR\/([\d.]+)/, "Opera"],
  [/\bSamsungBrowser\/([\d.]+)/, "Samsung Internet"],
  [/\bFirefox\/([\d.]+)/, "Firefox"],
  [/\bFxiOS\/([\d.]+)/, "Firefox"],
  [/\bCriOS\/([\d.]+)/, "Chrome"],
  [/\bChrome\/([\d.]+)/, "Chrome"],
  [/Version\/([\d.]+).*\bSafari\//, "Safari"],
];

const SISTEMAS = [
  [/\bWindows NT ([\d.]+)/, "Windows"],
  [/\bMac OS X ([\d_.]+)/, "macOS"],
  [/\bAndroid ([\d.]+)/, "Android"],
  [/\b(?:iPhone )?OS ([\d_]+) like Mac/, "iOS"],
  [/\bCrOS \S+ ([\d.]+)/, "ChromeOS"],
  [/\bLinux\b/, "Linux"],
];

function primeiroQueCasa(ua, pares) {
  for (const [rx, nome] of pares) {
    const m = rx.exec(ua);
    if (m) return { nome, versao: (m[1] || "").replace(/_/g, ".") || undefined };
  }
  return {};
}

/** Navegador e sistema, do `userAgentData` quando existe e da string como reserva. */
export function navegadorESistema({ ua = "", uaData = null } = {}) {
  const fora = {};
  // A forma MODERNA primeiro: `brands` vem declarado pelo navegador, sem adivinhação. A marca útil é a
  // que não é "Not/A)Brand" nem "Chromium" — essas duas existem para confundir quem adivinha.
  const marcas = (uaData && Array.isArray(uaData.brands) ? uaData.brands : [])
    .filter((b) => b && b.brand && !/not.a.brand|^chromium$/i.test(b.brand));
  if (marcas.length) {
    fora.browser = marcas[0].brand;
    if (marcas[0].version) fora.browser_version = String(marcas[0].version);
  }
  if (uaData && uaData.platform) fora.os = uaData.platform;
  if (uaData && typeof uaData.mobile === "boolean") fora.device_type = uaData.mobile ? "mobile" : "desktop";

  // E a reserva, que preenche só o que faltou — nunca sobrescreve o que o navegador declarou.
  if (ua) {
    // A reserva preenche o que faltou, campo a campo — e não "o nome e a versão juntos".
    //
    // Medido ao escrever o teste: o `userAgentData` dá `platform: "macOS"` e NÃO dá a versão do sistema
    // sem `getHighEntropyValues()`, que pede permissão. Com a condição por NOME, eu perdia a versão que
    // a string tinha ali do lado. Gastar uma permissão para recuperar o que já estava na mão seria a
    // troca errada.
    const n = primeiroQueCasa(ua, NAVEGADORES);
    if (!fora.browser && n.nome) fora.browser = n.nome;
    if (!fora.browser_version && n.versao) fora.browser_version = n.versao;
    const s2 = primeiroQueCasa(ua, SISTEMAS);
    if (!fora.os && s2.nome) fora.os = s2.nome;
    if (!fora.os_version && s2.versao) fora.os_version = s2.versao;
    if (!fora.device_type) fora.device_type = tipoDeDispositivo(ua);
  }
  return fora;
}

/** `mobile` | `tablet` | `desktop`, pela string. O tablet vem antes porque todo tablet diz mobile. */
export function tipoDeDispositivo(ua = "") {
  if (/\biPad\b/.test(ua) || (/\bAndroid\b/.test(ua) && !/\bMobile\b/.test(ua))) return "tablet";
  if (/\b(?:Mobile|iPhone|iPod|Android.*Mobile|Windows Phone)\b/.test(ua)) return "mobile";
  return "desktop";
}

/** `true` quando a pessoa declarou opt-out pelo Global Privacy Control. Ver `contextoDaSessao`. */
export function pediuParaNaoRastrear({ nav = null } = {}) {
  if (!nav) return false;
  // GPC é o sinal com peso legal (CCPA/CPA, e citado sob a LGPD). O `doNotTrack` legado fica FORA de
  // propósito: ele é ligado por padrão em navegador inteiro, foi abandonado pelos próprios fabricantes,
  // e honrá-lo apagaria a medição de quem nunca pediu nada.
  return nav.globalPrivacyControl === true || nav.globalPrivacyControl === "1";
}

/**
 * O contexto COMPLETO da visita. Vai uma vez por sessão, no `session_start`.
 *
 * Tudo por parâmetro: `nav` (navigator), `loc` (location), `doc` (document), `scr` (screen),
 * `win` (window, só para viewport e DPR). Ausência de qualquer um não levanta — devolve o que der.
 */
export function contextoDaSessao({ nav = null, loc = null, doc = null, scr = null, win = null } = {}) {
  const fora = {};
  const ua = (nav && nav.userAgent) || "";
  Object.assign(fora, navegadorESistema({ ua, uaData: nav && nav.userAgentData }));

  // A string CRUA. O comentário aqui dizia "uma vez por sessão e não por evento", e era FALSO: o
  // `tracker.track()` põe `user_agent` em TODO evento desde 29/09/2026, por decisão deliberada de
  // quando ele se perdia no topo do evento. Conferido no lake em 02/10/2026, no `indoor-media`:
  // `user_agent` está nos 9 tipos, e são 117 dos 450 bytes de um `scroll_depth` — 26% do evento, com
  // ZERO leitores medidos (`apps/`, frontend e as 268 regras). Deixo escrito porque reverter uma
  // decisão de três dias atrás com base na minha medição de bytes é decisão de produto, não minha; e
  // porque comentário que afirma o contrário do que o ar mostra é pior que comentário nenhum.
  if (ua) fora.user_agent = ua;
  if (nav && nav.language) fora.language = nav.language;
  if (nav && Array.isArray(nav.languages) && nav.languages.length > 1) {
    fora.languages = nav.languages.slice(0, 3).join(",");
  }
  if (nav && typeof nav.maxTouchPoints === "number") fora.touch_enabled = nav.maxTouchPoints > 0;
  if (nav && typeof nav.cookieEnabled === "boolean") fora.cookies_enabled = nav.cookieEnabled;
  if (nav && typeof nav.onLine === "boolean") fora.online = nav.onLine;
  const con = nav && (nav.connection || nav.mozConnection || nav.webkitConnection);
  if (con && con.effectiveType) fora.connection = con.effectiveType;
  if (con && typeof con.saveData === "boolean" && con.saveData) fora.save_data = true;

  // NÚMEROS SEPARADOS, e não `"412x915"`. Medido em 02/10/2026: `codeandsoul` e `agorafecha` já mandam
  // `viewport_width` e `viewport_height` como números, e são os dois produtores que existem. Texto
  // `WxH` obrigaria toda consulta a cortar a string para comparar com `> 768`, que é a pergunta real.
  if (scr && scr.width) fora.screen_width = scr.width;
  if (scr && scr.height) fora.screen_height = scr.height;
  if (win && win.innerWidth) fora.viewport_width = win.innerWidth;
  if (win && win.innerHeight) fora.viewport_height = win.innerHeight;
  if (win && win.devicePixelRatio && win.devicePixelRatio !== 1) {
    fora.pixel_ratio = Math.round(win.devicePixelRatio * 100) / 100;
  }

  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) fora.timezone = tz;
  } catch { /* Intl ausente: o fuso fica de fora, e ausência não é zero */ }
  try {
    // `getTimezoneOffset()` CRU, sem inverter o sinal — ele dá +180 em America/Sao_Paulo, e era aqui
    // que eu divergia. Medido em 02/10/2026 nos 802 `session_start` do tenant `synqi3`:
    // `timezone_offset = 180` com `timezone = America/Sao_Paulo`. Eu mandava `-180` sob o nome
    // `timezone_offset_minutes`. Renomear sem corrigir o sinal seria PIOR que divergir no nome: duas
    // convenções opostas sob a mesma chave, e a consulta que somasse as duas daria zero.
    fora.timezone_offset = new Date().getTimezoneOffset();
  } catch { /* idem */ }

  const ref = (doc && doc.referrer) || "";
  fora.referrer_type = tipoDaOrigem(ref, loc && loc.hostname);
  const host = hostDaOrigem(ref);
  if (host) fora.referrer_host = host;
  // A ATERRAGEM e a localização, com as grafias que os 802 `session_start` do tenant `synqi3` já
  // usam: `landing_page` guarda o CAMINHO (medido: `/`, `/auth`), não a URL — o nome diz "page" e o
  // valor é o caminho, e manter o valor deles importa mais que achar o nome melhor.
  if (loc && loc.pathname) fora.landing_page = loc.pathname;
  if (loc && loc.pathname) fora.path = loc.pathname;
  if (loc && loc.href) fora.url = loc.href;
  if (loc && loc.search) fora.search = loc.search;
  if (loc && loc.hash) fora.hash = loc.hash;
  if (doc && doc.title) fora.title = doc.title;

  return fora;
}

//: O SUBCONJUNTO que viaja em TODO evento. Curto de propósito: medido em 02/10/2026, um evento do SDK
//: já ocupa 515 bytes e a fila corta o lote em 8.192 (o teto do portão), então 15 eventos cabem numa
//: requisição. Mandar o contexto inteiro em cada um derrubaria isso para 8, dobrando as requisições e
//: a quota consumida do inquilino.
//:
//: Estes quatro ficam porque a CONDIÇÃO de uma regra e a personalização os leem no próprio evento: "se
//: for mobile e vier de social, mostre X". Os outros se resolvem por `session_id` contra o
//: `session_start`, que é onde o contexto inteiro mora.
const COMPACTO = ["device_type", "browser", "os", "referrer_type"];

/** O contexto curto, para enriquecer cada evento. Nunca sobrescreve o que o chamador escreveu. */
export function contextoCompacto(completo = {}) {
  const fora = {};
  for (const k of COMPACTO) {
    if (completo[k] !== undefined && completo[k] !== null) fora[k] = completo[k];
  }
  return fora;
}

export { COMPACTO };
